import { useContext, useCallback, useMemo } from 'react';

import { usePageRestrictionsContext } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as LOCALSTORAGE_KEYS } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { CREATE_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type { Creatable, CanHandle, Handle } from '../createContentFromTemplateTypes.ts';
import { processDraftUrl } from '../helpers.ts';

import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useNavigation } from './useNavigation.ts';
import type {
	useBlogpostCreatableMutation$data,
	useBlogpostCreatableMutation$variables,
} from './__generated__/useBlogpostCreatableMutation.graphql.ts';

export const createBlogpostMutation: GraphQLTaggedNode = graphql`
	mutation useBlogpostCreatableMutation($spaceKey: String!, $title: String) {
		createContentContextual(
			input: { spaceKey: $spaceKey, title: $title, type: "blogpost", status: DRAFT }
		) {
			id
			links {
				editui
			}
		}
	}
`;

export const useBlogpostCreatable = (): Creatable => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const navigate = useNavigation();
	const isEditorPage = useIsEditorPage();

	const [createBlogpostMutationFn] = useMutationInterop<
		useBlogpostCreatableMutation$data,
		useBlogpostCreatableMutation$variables
	>(createBlogpostMutation);

	// Restrictions only affect the editor's permission notice, not creation from View Page.
	const { restrictions } = usePageRestrictionsContext({ skipWhenDeferred: !isEditorPage });

	const canHandle: CanHandle = useCallback(
		(template) =>
			template.itemModuleCompleteKey ===
			'com.atlassian.confluence.plugins.confluence-create-content-plugin:create-blog-post',
		[],
	);

	const handle: Handle = useCallback(
		async (_, spaceKey, options = {}) => {
			const { title, openContentInNewTab } = options;

			const createBlogpostResponse = await createBlogpostMutationFn({
				variables: {
					spaceKey,
					title,
				},
			});
			const editui = createBlogpostResponse?.data?.createContentContextual?.links?.editui;
			const url = processDraftUrl(editui);

			// if your in the editor, pressing browser back shouldn't land back you in the previous page editor.
			if (isEditorPage) {
				if (restrictions?.hasRestrictions) {
					localStorage.setItem(LOCALSTORAGE_KEYS.BLOGPOST_PERMISSIONS_NOTICE, true);
				}
				navigate(url, {
					openContentInNewTab,
					isReplaceAction: true,
				});
			} else {
				navigate(url, {
					openContentInNewTab,
				});
			}

			experienceTracker.succeed({ name: CREATE_PAGE_EXPERIENCE });

			return true;
		},
		[createBlogpostMutationFn, experienceTracker, isEditorPage, navigate, restrictions],
	);

	return useMemo(() => ({ canHandle, handle }), [canHandle, handle]);
};
